import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Star, Info, Plus, Minus, ArrowLeft, ShoppingCart, Loader } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { API_ENDPOINTS, fetchWithTimeout, getImageUrl } from '../config/api';

interface ProductVariation {
  variant_id: string;
  variant_name: string;
  variant_purchasing_price: string;
  variant_refilling_price: string;
  discount_refilling_price: string;
  discount_purchasing_price: string;
  variant_image: string;
  variant_quantity: string;
}

interface Product {
  product_id: string;
  product_name: string;
  product_description: string;
  product_image: string;
  delivery_fee: number;
  variations: ProductVariation[];
}

export const ProductDetails: React.FC = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { dispatch } = useCart();
  const [product, setProduct] = useState<Product | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [size, setSize] = useState<'6kg' | '13kg'>('6kg');
  const [type, setType] = useState<'new' | 'refill'>('refill');
  const [quantity, setQuantity] = useState(1);
  const [showInstructions, setShowInstructions] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const formData = new URLSearchParams();
        formData.append('product_id', productId?.split('-')[0] || '');
        const response = await fetchWithTimeout(API_ENDPOINTS.GET_PRODUCT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          mode: 'cors',
          body: formData
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        
        if (data.success) {
          setProduct(data.data);
        } else {
          setError(data.message || 'Failed to fetch product');
        }
      } catch (err) {
        console.error('Error fetching product:', err);
        setError('Failed to load product. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    if (productId) {
      fetchProduct();
    }
  }, [productId]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader className="h-8 w-8 animate-spin text-green-600" />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8 pt-24">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            {error || 'Product not found'}
          </h2>
          <button
            onClick={() => navigate('/')}
            className="mt-4 text-green-600 hover:text-green-700"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  const getVariationsBySize = (variations: ProductVariation[], size: string) => {
    const filtered = variations.filter(v => {
      const variantName = v.variant_name.toLowerCase();
      const sizeNumber = size.split('kg')[0];
      const sizeVariations = [
        size.toLowerCase(),
        `${sizeNumber} kg`,
        `${sizeNumber} Kg`,
        `${sizeNumber}kg`,
        `${sizeNumber} KG`,
        `${sizeNumber}KG`,
      ];
      
      return sizeVariations.some(sizeVariation => 
        variantName.includes(sizeVariation)
      );
    });

    return filtered[0]; // Return the first matching variation
  };

  const selectedVariation = getVariationsBySize(product.variations, size);
  const price = type === 'new' 
    ? parseFloat(selectedVariation?.variant_purchasing_price || '0')
    : parseFloat(selectedVariation?.variant_refilling_price || '0');

  const handleAddToCart = () => {
    if (!selectedVariation) return;

    dispatch({
      type: 'ADD_TO_CART',
      payload: {
        productId: `${product.product_id}-${selectedVariation.variant_id}`,
        brand: product.product_name,
        size,
        type,
        quantity,
        price,
      }
    });

    navigate('/cart');
  };

  const renderStars = (rating: number) => {
    return [...Array(5)].map((_, index) => (
      <Star
        key={index}
        className={`h-5 w-5 ${
          index < rating
            ? 'text-yellow-400 fill-current'
            : 'text-gray-300'
        }`}
      />
    ));
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8 pt-24">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center text-gray-600 hover:text-gray-900 mb-8"
      >
        <ArrowLeft className="h-5 w-5 mr-2" />
        Back
      </button>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
        <div className="md:flex">
          {/* Product Image */}
          <div className="md:w-1/2">
            <img 
              src={getImageUrl(selectedVariation?.variant_image || product.product_image)} 
              alt={product.product_name}
              className="w-full h-96 object-cover"
            />
          </div>

          {/* Product Details */}
          <div className="md:w-1/2 p-8">
            <div className="flex justify-between items-start mb-4">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                {product.product_name}
              </h1>
              <button
                onClick={() => setShowInstructions(!showInstructions)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
                title="Usage Instructions"
              >
                <Info className="h-6 w-6 text-gray-600 dark:text-gray-400" />
              </button>
            </div>

            <p className="text-gray-600 dark:text-gray-300 text-lg mb-4">
              {product.product_description}
            </p>

            {/* Rating */}
            <div className="flex items-center space-x-2 mb-6">
              {renderStars(4)}
              <span className="text-gray-600 dark:text-gray-400 ml-2">
                (0 reviews)
              </span>
            </div>

            {/* Usage Instructions */}
            {showInstructions && (
              <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                  How to Use:
                </h3>
                <ol className="list-decimal list-inside space-y-2 text-gray-700 dark:text-gray-300">
                  <li>Ensure all gas knobs are turned off</li>
                  <li>Connect regulator firmly to cylinder valve</li>
                  <li>Turn on the cylinder valve slowly</li>
                  <li>Check for leaks using soapy water</li>
                  <li>If you smell gas, turn off immediately</li>
                </ol>
              </div>
            )}

            {/* Product Options */}
            <div className="space-y-6">
              {/* Size Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Size
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() => setSize('6kg')}
                    className={`px-4 py-3 rounded-lg border-2 text-center ${
                      size === '6kg'
                        ? 'border-green-600 bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    6 KG
                  </button>
                  <button
                    onClick={() => setSize('13kg')}
                    className={`px-4 py-3 rounded-lg border-2 text-center ${
                      size === '13kg'
                        ? 'border-green-600 bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    13 KG
                  </button>
                </div>
              </div>

              {/* Type Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Type
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() => setType('new')}
                    className={`px-4 py-3 rounded-lg border-2 text-center ${
                      type === 'new'
                        ? 'border-green-600 bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    New
                  </button>
                  <button
                    onClick={() => setType('refill')}
                    className={`px-4 py-3 rounded-lg border-2 text-center ${
                      type === 'refill'
                        ? 'border-green-600 bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    Refill
                  </button>
                </div>
              </div>

              {/* Quantity Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Quantity
                </label>
                <div className="flex items-center justify-between border rounded-lg p-3 bg-gray-50 dark:bg-gray-700">
                  <button 
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="p-2 rounded-full hover:bg-white dark:hover:bg-gray-600 hover:shadow-sm transition-all"
                  >
                    <Minus className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                  </button>
                  <span className="text-xl font-medium text-gray-900 dark:text-white">
                    {quantity}
                  </span>
                  <button 
                    onClick={() => setQuantity(quantity + 1)}
                    className="p-2 rounded-full hover:bg-white dark:hover:bg-gray-600 hover:shadow-sm transition-all"
                  >
                    <Plus className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                  </button>
                </div>
              </div>

              {/* Price and Add to Cart */}
              <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      Total Price:
                    </span>
                    <div className="text-3xl font-bold text-gray-900 dark:text-white">
                      KSH {price * quantity}
                    </div>
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    KSH {price} per unit
                  </div>
                </div>

                <div className="flex space-x-4">
                  <button
                    onClick={handleAddToCart}
                    disabled={!selectedVariation}
                    className="flex-1 bg-green-600 text-white py-4 rounded-lg text-lg font-semibold hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Add to Cart
                  </button>
                  <button
                    onClick={() => navigate('/cart')}
                    className="flex items-center justify-center px-6 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    <ShoppingCart className="h-6 w-6" />
                    <span className="ml-2">View Cart</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};